import React from 'react';
import StylePreview from './StylePreview';
import { BASIC_STYLES, DEFAULT_BASIC_STYLE_ID } from '../utils/basicStyles';

/**
 * Picker for the Basic-mode basemap styles.
 *
 * Visually mirrors StaticImageSlideshow (scrollable grid, 4:3 tiles, ring
 * around the selected one) so the layout doesn't shift when switching
 * tabs at the dashboard level. Differs in two ways:
 *   - Each tile is a real <StylePreview>, locked to the user's print
 *     rectangle and rendered with their activity polylines on top, so
 *     they see what the printed map will look like with each style.
 *   - Selection is direct (click → applies). No "Select Style" button
 *     because there's nothing else to confirm.
 *
 * `children` slot mirrors StaticImageSlideshow so the parent passes the
 * same Customize / styles-shared buttons.
 */
const BasicStylesGrid = ({
  selectedStyleId,
  onSelect,
  rectangleBounds,
  activities,
  activitySettings,
  children,
}) => {
  const activeId = selectedStyleId || DEFAULT_BASIC_STYLE_ID;

  return (
    // w-full is essential: each tile is a Leaflet <MapContainer> which has
    // no intrinsic width, so without it the parent flex column collapses
    // the whole grid down to almost nothing (StaticImageSlideshow only
    // grows naturally because its <img> tags do have intrinsic widths).
    <div className="relative space-y-4 w-full">
      <div className="grid grid-cols-3 lg:grid-cols-2 gap-2 lg:gap-3 w-full max-h-[calc(1.1*10rem)] md:max-h-[calc(1.8*10rem)] lg:max-h-[calc(2.4*10rem)] overflow-y-auto p-2 mb-4">
        {BASIC_STYLES.map((s) => {
          const isSelected = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onSelect?.(s.id)}
              className={`aspect-[4/3] relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 text-left ${isSelected ? 'ring-2 ring-red-500' : 'hover:ring-2 hover:ring-gray-300'
                }`}
            >
              {/* Thumbnails intentionally show only the basemap — no
                  polylines. The user judges activities-on-top quality
                  via the big live preview to the right. */}
              <div className="absolute inset-0">
                <StylePreview
                  style={s}
                  bounds={rectangleBounds}
                  compact
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2
                              group-hover:bg-opacity-70 transition-colors">
                <div className="flex justify-between items-center">
                  <p className="text-xs truncate">{s.label}</p>
                  <p className="text-[10px] truncate text-gray-300 ml-2">{s.sublabel}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex justify-center space-x-2 items-center">
        <div className="space-x-2 flex">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BasicStylesGrid;
